import { useEffect, useState } from 'react';

/**
 * A clock that makes the component re-render.
 *
 * The compose dialog decides whether the chosen send time has passed. That
 * decision changes with NO user input and NO redux change — the only thing that
 * moves is the wall clock — so a dialog opened at 14:29 for a 14:30 send sat
 * there past 14:35 still reporting the time as fine, and Send stayed live. The
 * edge case is not exotic: it is what happens whenever someone opens the dialog,
 * gets distracted, and comes back.
 *
 * Ten seconds, not one: the gate has a one-minute grace, so the worst this
 * granularity costs is that the block appears between 60 and 70 seconds after
 * the send time rather than at exactly 60. A per-second timer would buy nothing
 * a person could perceive and would re-render the whole dialog sixty times a
 * minute.
 *
 * Returns a millisecond timestamp so callers pass it into a PURE predicate
 * rather than each reaching for `Date.now()` at a slightly different instant.
 */
export const NOW_TICK_MS = 10_000;

export const useNowTicker = (intervalMs: number = NOW_TICK_MS): number => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => { setNow(Date.now()); }, intervalMs);
        return () => { clearInterval(id); };
    }, [intervalMs]);
    return now;
};
